/**
 * Maps local guest-cart lines to POST /dindin-web/place-order body (Laravel WebOrderController).
 *
 * Cart addons:
 * - Real modifiers: `id` = modifier option UUID
 * - Combination extras from customize modal: `id` = `extra-{menuItemUuid}`, optional `qty` count
 *
 * Payment (optional; when provided all card fields are validated to match API rules):
 * - card_number (13–19 digits)
 * - exp_date (MMYY or MMYYYY digits)
 * - cvv (3–4 digits)
 * - cardholder_first_name / cardholder_last_name (optional)
 */

/**
 * @param {{ cardNumber?: string, expDate?: string, cvv?: string, cardholderFirstName?: string, cardholderLastName?: string }} fields
 * @returns {{ card_number: string, exp_date: string, cvv: string, cardholder_first_name?: string, cardholder_last_name?: string }}
 */
export function buildPaymentPayload({
  cardNumber = '',
  expDate = '',
  cvv = '',
  cardholderFirstName = '',
  cardholderLastName = '',
}) {
  const card_number = String(cardNumber || '').replace(/\D/g, '');
  if (card_number.length < 13 || card_number.length > 19) {
    throw new Error('Enter a valid card number (13–19 digits).');
  }

  const exp_date = String(expDate || '').replace(/\D/g, '');
  if (exp_date.length !== 4 && exp_date.length !== 6) {
    throw new Error('Enter card expiry as MM/YY.');
  }

  const cvvDigits = String(cvv || '').replace(/\D/g, '');
  if (cvvDigits.length < 3 || cvvDigits.length > 4) {
    throw new Error('Enter a valid CVV (3–4 digits).');
  }

  const payment = {
    card_number,
    exp_date,
    cvv: cvvDigits,
  };

  const first = String(cardholderFirstName || '').trim();
  const last = String(cardholderLastName || '').trim();
  if (first) payment.cardholder_first_name = first;
  if (last) payment.cardholder_last_name = last;

  return payment;
}

/** @param {{ name?: string } | null | undefined} customer */
export function splitCustomerName(customer) {
  const full = String(customer?.name || '').trim();
  if (!full) return { firstName: '', lastName: '' };
  const parts = full.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

export function buildWebOrderPayload({
  restaurantId,
  branchId = null,
  lines = [],
  orderType = 'pickup',
  notes = '',
  deliveryAddress = '',
  deliveryCoords = null,
  payment = null,
}) {
  const rid = Number(restaurantId);
  if (!Number.isFinite(rid) || rid <= 0) {
    throw new Error('Invalid restaurant');
  }

  const apiLines = [];

  for (const line of lines) {
    const itemUuid = String(line?.itemId || '').trim();
    if (!itemUuid) continue;

    const lineQty = Math.max(1, Number(line.qty || 1));
    const modifierOptionUuids = [];
    const comboExtras = []; // { menu_item_uuid, quantity }

    for (const a of line.addons || []) {
      const sid = String(a?.id ?? '').trim();
      if (!sid) continue;
      if (sid.startsWith('extra-')) {
        const menuUuid = sid.slice('extra-'.length).trim();
        if (!menuUuid) continue;
        const perItemQty = Math.max(1, Number(a.qty ?? 1));
        comboExtras.push({
          menu_item_uuid: menuUuid,
          quantity: perItemQty * lineQty,
        });
      } else {
        const mq = Math.max(1, Number(a.qty ?? 1));
        for (let i = 0; i < mq; i += 1) {
          modifierOptionUuids.push(sid);
        }
      }
    }

    apiLines.push({
      menu_item_uuid: itemUuid,
      quantity: lineQty,
      ...(line?.variant?.id ? { menu_item_variation_uuid: String(line.variant.id) } : {}),
      ...(modifierOptionUuids.length ? { modifier_option_uuids: modifierOptionUuids } : {}),
      ...(line?.notes ? { notes: String(line.notes) } : {}),
    });

    for (const ex of comboExtras) {
      apiLines.push({
        menu_item_uuid: ex.menu_item_uuid,
        quantity: ex.quantity,
      });
    }
  }

  if (!apiLines.length) {
    throw new Error('Cart has no valid items');
  }

  const payload = {
    restaurant_id: rid,
    order_type: orderType,
    lines: apiLines,
  };

  if (branchId != null && branchId !== '') {
    const bid = Number(branchId);
    if (Number.isFinite(bid) && bid > 0) payload.branch_id = bid;
  }

  const n = String(notes || '').trim();
  if (n) payload.notes = n;

  const addr = String(deliveryAddress || '').trim();
  if (addr && orderType === 'delivery') payload.delivery_address = addr;

  const latitude = Number(deliveryCoords?.latitude);
  const longitude = Number(deliveryCoords?.longitude);
  const hasValidCoords =
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180;

  if (orderType === 'delivery' && hasValidCoords) {
    payload.delivery_latitude = latitude;
    payload.delivery_longitude = longitude;
  }

  if (payment && typeof payment === 'object') {
    payload.payment = payment;
  }

  return payload;
}
